/**
 * Navigation data for the Pickcel website
 */

import { env } from "../environment";

export type Section = "products" | "customers" | "resources" | "main";

export interface MenuCategory  {
  category?: string;
  section?: "LEFT" | "RIGHT" | "MIDDLE";
  items: MenuItem[];
}

export interface MenuItem {
  label: string;
  href: string;
  className?: string;
  badge?: string;
  icon?: string;
  description?: string;
  src?: string;
}

export interface MenuSection {
  label: string;
  items: MenuCategory[];
}
// add base url to the hrefs
export const baseUrl = "https://www.pickcel.com";

export const NAVIGATION_MENU: Record<Section, MenuSection> = {
  products: {
    label: "Products",
    items: [
      {
        category: "Core Platform",
        section: "LEFT",
        items: [
          { label: "Software Features", href: `/digital-signage-software`, icon: "Monitor" },
          {
            label: "Pickcel Comms",
            href: `/solutions/multi-channel-employee-communication-platform`,
            icon: "MessagesSquare",
          },
          { label: "Templates", href: `/digital-signage-templates`, icon: "FileText" },
          { label: "Security & Compliance", href: `/digital-signage-security`, icon: "Shield" },
        ],
      },
      {
        category: "Extensions & Apps",
        section: "MIDDLE",
        items: [
          {
            label: "Mobile App",
            href: `/digital-signage-content-management-mobile-app`,
            icon: "Smartphone",
          },
          { label: "App Integrations", href: `/digital-signage-apps`, icon: "LayoutGrid" },
        ],
      },


      {
        category: "Enterprise",
        section: "LEFT",
        items: [
          { label: "On-Premise", href: `/solutions/on-premise-digital-signage`, icon: "Server" },
          { label: "Integrations & Automation", href: `/digital-signage-integrations`, icon: "Wifi" },
          { label: "Our Services", href: `/digital-signage-services`, icon: "Headphones" },
        ],
      },
      {
        section: "RIGHT",
        items:[
        {
          src: env.NEXT_PUBLIC_CDN_URL + "/images/shared/header/what-is-digital-signage.Pf4o9GkkrvKb.webp",
          label: "What is Digital Signage?",
          href: `/digital-signage`,
          description: "Learn the basics of digital signage and how it can transform your business.",
        },
      ]},
      {
        category: "Digital Signage Players",
        section: "MIDDLE",
        items: [
        { label: "Android", href: "/digital-signage-player/android", icon: "Tablet"},
        { label: "Samsung Tizen", href: "/digital-signage-player/samsung-tizen", icon: "Monitor" },
        { label: "BrightSign", href: "/digital-signage-player/brightsign", badge: "New", icon: "Monitor" },
        { label: "Amazon Signage Stick", href: `/digital-signage-player/amazon-signage-stick`, icon: "Monitor" },

        { label: "View all Players", href: "/digital-signage-player" },
      ]
      },
    ],
  },
  customers: {
    label: "Solutions",
    items: [
      {
        category: "Industries We Serve",
        section: "LEFT",
        items: [
          { label: "Office", href: `/solutions/office-digital-signage`, icon: "Building2" },
          { label: "Retail", href: `/industries/retail-digital-signage-software`, icon: "Store" },
          {
            label: "Restaurant",
            href: `/industries/restaurant-digital-signage-software`,
            icon: "UtensilsCrossed",
          },
          {
            label: "Manufacturing",
            href: `/industries/manufacturing-digital-signage-software`,
            icon: "Factory",
          },
          {
            label: "Hospitality",
            href: `/industries/hospitality-digital-signage-software`,
            icon: "Hotel",
          },
          {
            label: "Healthcare",
            href: `/industries/healthcare-digital-signage-software`,
            icon: "Stethoscope",
          },
          {
            label: "Transportation",
            href: `/industries/transportation-digital-signage-software`,
            icon: "Bus",
          },
          {
            label: "Education",
            href: `/industries/education-digital-signage-software`,
            icon: "GraduationCap",
          },
          { label: "View all Industries", href: `/industries` },
        ],
      },

      // Solutions section
      {
        category: "Solutions",
        section: "MIDDLE",
        items: [
          { label: "Dashboard", href: `/digital-signage-dashboard`, icon: "LayoutDashboard" },
          { label: "FIDS", href: `/solutions/digital-signage-flight-information`, icon: "Plane" },
          // { label: "Wayfinding", href: `/solutions/wayfinding-signage`, icon: "Signpost" },
          { label: "Digital Menu board", href: `/solutions/digital-menu-board`, icon: "ClipboardList" },
          { label: "Car Dealership App", href: `/solutions/car-dealership-digital-signage`, icon: "Car" },
          { label: "Wall Craft", href: `/solutions/video-wall-software`, icon: "LayoutTemplate" },
          { label: "Event Stream", href: `/solutions/digital-signage-streaming`, icon: "Radio" },
          {
            label: "Transport PIS",
            href: `/industries/passenger-information-display-system-software`,
            icon: "Bus",
          },
          { label: "Social Wall", href: `/solutions/digital-signage-social-wall`, icon: "Share2" },
          { label: "View all Solutions", href: `/digital-signage-solution` },
        ],
      },
      {
        section: "RIGHT",
        items: [
          {
          src: env.NEXT_PUBLIC_CDN_URL + "/images/shared/header/amazon.Oob1rB5qWuTE.webp",
            label: "See How Pickcel  Helped Amazon ",
            href: `/case-studies/amazon` },
        ],
      },

      {
        category: "Case Studies",
        section: "RIGHT",
        items: [
          {
            label: "Mercedes-Benz",
            href: `/case-studies/mercedes-benz-corporate-communication`,
            icon: "Car",
          },
          {
            label: "Accenture",
            href: `/case-studies/accenture-and-pickcel-corporate-digital-signage`,
            icon: "Briefcase",
          },
          { label: "Magnolia Bakery", href: `/case-studies/magnolia-bakery`, badge: "New", icon: "Cake" },
          { label: "View all", href: `/case-studies` },
        ],
      },
    ],
  },
  resources: {
    label: "Resources",
    items: [
      // All Resources section
      {
        category: "All Resources",
        section: "LEFT",
        items: [
          { label: "Digital Signage Blog", href: `${baseUrl}/blog/`, icon: "Newspaper" },
          { label: "Learn", href: `/learn`, icon: "BookOpen" },
          { label: "Digital Signage Gallery", href: `/digital-signage-gallery`, icon: "Images" },
          { label: "Case Studies", href: `/case-studies`, icon: "FolderOpen" },
          { label: "Support", href: `https://docs.pickcel.com`, icon: "HelpCircle" },
          { label: "Download", href: `/download`, icon: "Download" },
        ],
      },
      {
        section: "RIGHT",
        items: [
          {
            src: env.NEXT_PUBLIC_CDN_URL + "/images/shared/header/partnership.P-PMlY7M6DLK.webp",
            label: "Partnerships",
            href: `/partners/`
          },
        ],
      },      {
        category: "Partnerships",
        section: "RIGHT",
        items: [
          { label: "White-Label", href: `/partners/white-label-digital-signage-software`, icon: "Tag" },
          { label: "Reseller", href: `/partners/reseller-digital-signage-software`, icon: "Handshake" },
        ],
      },
    ],
  },
  main: {
    label: "Main",
    items: [
      { items:[
      { label: "Pricing", href: `/pricing` },
      { label: "Contact Us", href: `/company/contact` },
      { label: "Login", href: "https://console.pickcel.com" },
      { label: "Start Free Trial", href: "https://console.pickcel.com/auth/register" },
      ]},
    ],
  },
} as const;

// Helper functions for easier access
export const getMenuSection = (section: Section) => NAVIGATION_MENU[section];
export const getMenuItems = (section: Section) => NAVIGATION_MENU[section].items;
export const getAllMenuSections = () => Object.keys(NAVIGATION_MENU) as Section[];

